<template>
  <div>
    <v-fade-transition>
      <v-overlay v-if="!isPhotoUpload" :absolute="absolute" :value="overlay">
        <v-progress-circular
          :size="50"
          color="primary"
          indeterminate
        ></v-progress-circular>
      </v-overlay>
    </v-fade-transition>
    <v-avatar
      size="100%"
      rounded="0"
      tile
      :color="section == 'people' ? 'pageHeader lighten-1' : ''"
      :class="[section == 'people' ? 'profile' : '']"
    >
      <v-img
        v-if="profilePhoto && section == 'people'"
        :src="profilePhoto"
        height="250"
        width="210"
      ></v-img>
      <v-img
        v-else-if="profilePhoto && section == 'user'"
        :src="profilePhoto"
        max-height="100"
        contain
        class="grey lighten-4"
      ></v-img>
      <v-img
        v-else-if="section == 'people'"
        src="@/assets/images/No-Photo.jpg"
        height="250"
      ></v-img>
      <v-img
        v-else
        max-height="100"
        contain
        class="grey lighten-4"
        src="@/assets/images/No-Photo.jpg"
      ></v-img>
    </v-avatar>
    <v-row v-if="isPhotoSizeMax">
      <v-col cols="12" no-gutters class="pb-0">
        <span style="color: var(--v-error-base); ma">{{
          $t("photoSizeErrorMsg", {
            maxsize: formatBytes(maxProfileImgSize),
          })
        }}</span>
      </v-col>
    </v-row>
    <v-row align="center">
      <v-col cols="12" sm="12" md="12" class="m-auto d-flex justify-center">
        <v-tooltip right v-if="ispermitted">
          <template v-slot:activator="{ on, attrs }">
            <a-btn
              v-bind="attrs"
              v-on="on"
              v-if="ispermitted"
              class="mt-2 primaryDark"
              small
              @click="$refs.inputUpload.click()"
              :disabled="!ispermitted"
              :block="!profilePhoto"
            >
              <v-icon class="mr-1" size="16">mdi-camera</v-icon>
              {{
                profilePhoto
                  ? $t("people.lblChangePhoto")
                  : $t("people.lblUpload")
              }}
              <input
                v-show="false"
                ref="inputUpload"
                :name="uploadFieldName"
                type="file"
                accept=".png, .jpg, .jpeg"
                @change="filesChange($event.target.files)"
              />
            </a-btn>
          </template>
          <span>
            {{
              profilePhoto
                ? $t("people.tlChangePhoto")
                : $t("people.lblUploadPhoto")
            }}</span
          >
        </v-tooltip>
        <v-tooltip right v-if="profilePhoto">
          <template v-slot:activator="{ on, attrs }">
            <a-btn
              v-bind="attrs"
              v-on="on"
              v-if="profilePhoto && ispermitted"
              min-width="32"
              color="primaryDark"
              small
              width="32"
              class="mt-2 ml-2"
              @click="clearPhoto"
              :disabled="!ispermitted"
            >
              <v-icon size="16" dark> mdi-delete </v-icon>
            </a-btn>
          </template>
          <span>{{ $t("people.lblDeletePhoto") }}</span>
        </v-tooltip>
      </v-col>
    </v-row>
    <message-sheet
      :showMessage="displayMessage"
      :message="message"
      :messageType="messageType"
      @close="displayMessage = false"
    ></message-sheet>
    <confirm-dialog ref="confirm" />
  </div>
</template>

<script>
import _ from "lodash";
import ConfirmDialog from "../ConfirmDialog.vue";
import MessageSheet from "../MessageSheet.vue";
import { formatBytes } from "../../utils/util";
import { MAX_PROFILE_PHOTO_SIZE } from "../../utils/constant";

export default {
  name: "ProfilePhoto",
  props: {
    value: {
      type: String | null,
    },
    ispermitted: {
      type: Boolean,
      default: true,
    },
    section: {
      type: String,
      default: "user",
    },
  },
  components: {
    ConfirmDialog,
    MessageSheet,
  },
  data() {
    return {
      isPhotoUpdated: false,
      isPhotoUpload: true,
      isPhotoSizeMax: false,
      overlay: true,
      absolute: true,
      uploadFieldName: "photos",
      displayMessage: false,
      message: "",
      messageType: "success",
      maxProfileImgSize: MAX_PROFILE_PHOTO_SIZE,
    };
  },
  computed: {
    profilePhoto: {
      get() {
        return String(this.value || "").trim();
      },
      set(value) {
        this.$emit("input", value || "");
      },
    },
  },
  methods: {
    filesChange,
    wait,
    clearPhoto,
    showMessageSheet,
  },
  watch: {
    isPhotoUpload(value) {
      this.$emit("uploading", Boolean(value));
    },
  },
};
async function clearPhoto() {
  if (
    await this.$refs.confirm.open(
      this.$t("lblConfirmRemoval"),
      this.$t("msgConfirmRemovalMsg"),
      { yesNoButtons: true }
    )
  ) {
    this.profilePhoto = "";
    this.$refs.inputUpload.value = null;
    this.isPhotoUpdated = true;
    this.overlay = false;
  }
}
function showMessageSheet(message, messageType) {
  this.displayMessage = true;
  this.message = message;
  this.messageType = messageType;
}

async function wait(ms) {
  let promise = new Promise((res, rej) => {
    setTimeout(() => res("Now it's done!"), ms);
  });

  await promise;
}

async function filesChange(fileList) {
  const files = fileList;
  let filesWithStatus = [];
  if (files.length === 0) {
    return false;
  }

  for (let item of files) {
    if (!["image/png", "image/jpeg", "image/gif"].includes(item.type)) {
      filesWithStatus.push(this.$t("fileErrorMsg", { filename: item.name }));
    } else if (item.size > this.maxProfileImgSize) {
      filesWithStatus.push(
        this.$t("fileSizeErrorMsg", {
          maxsize: formatBytes(this.maxProfileImgSize),
        })
      );
    }
  }
  if (filesWithStatus.length > 0) {
    this.$refs.inputUpload.value = null;
    this.showMessageSheet(filesWithStatus.join("/n"), "error");
    return false;
  }
  this.isPhotoUpload = false;
  this.overlay = true;
  const fileReader = new FileReader();
  fileReader.addEventListener("load", async () => {
    await wait(1500);
    this.profilePhoto = fileReader.result;
    this.isPhotoUpload = true;
    this.isPhotoUpdated = true;
  });
  fileReader.readAsDataURL(files[0]);
}
</script>
